
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiShoppingBag, FiPackage, FiUsers, FiBarChart2, FiLogOut, FiHome, FiFileText, FiShield } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import DashboardHome from '../components/dashboard/DashboardHome';
import ProductManagement from '../components/dashboard/ProductManagement';
import SupplierManagement from '../components/dashboard/SupplierManagement';
import Statistics from '../components/dashboard/Statistics';
import OrderManagement from '../components/dashboard/OrderManagement';
import UserManagement from '../components/dashboard/UserManagement';
import InvoicePage from '../pages/InvoicePage';

const Dashboard = () => {
  // Nouvelles couleurs de la charte graphique
  const primaryColor = 'text-yellow-400';
  const primaryBg = 'bg-yellow-400';
  const primaryHover = 'hover:bg-yellow-500';
  const primaryLight = 'bg-yellow-400/10';
  const primaryBorder = 'border-yellow-400';
  
  const secondaryColor = 'text-gray-400';
  const secondaryBg = 'bg-gray-400';
  const secondaryHover = 'hover:bg-gray-500';
  const secondaryLight = 'bg-gray-100';
  const secondaryBorder = 'border-gray-300';

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { user, logout, loading, initialized, isAdmin, isMainAdmin, isSubAdmin, isSupAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialized && !loading && !isAdmin) {
      navigate('/login');
    }
  }, [loading, initialized, isAdmin, navigate]);

  // Gérer le redimensionnement
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fermer la sidebar au démarrage si mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // ⭐ 1. Affiche un loader pendant le chargement
  if (loading || !initialized) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${primaryBorder} mx-auto`}></div>
          <p className={`mt-4 ${secondaryColor} font-medium`}>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    navigate('/login');
    logout();
  };

  const goToShop = () => {
    navigate('/');
  };

  // Onglets pour Admin Principal
  const adminMenuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Accueil', exact: true },
    { path: '/dashboard/products', icon: FiPackage, label: 'Gestion Produits' },
    { path: '/dashboard/suppliers', icon: FiUsers, label: 'Gestion Fournisseurs' },
    { path: '/dashboard/orders', icon: FiFileText, label: 'Gestion Commandes' },
    { path: '/dashboard/users', icon: FiShield, label: 'Gestion Utilisateurs' },
    { path: '/dashboard/statistics', icon: FiBarChart2, label: 'Statistiques' },
  ];

  // Onglets pour Sous-admin (restreint)
  const subAdminMenuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Accueil', exact: true },
    { path: '/dashboard/products', icon: FiPackage, label: 'Gestion Produits' },
    { path: '/dashboard/orders', icon: FiFileText, label: 'Gestion Commandes' },
  ];

  // Onglets pour Operationnel (restreint)
  const supAdminMenuItems = [
    { path: '/dashboard/products', icon: FiPackage, label: 'Gestion Produits' },
  ];

  // Sélectionner les onglets en fonction du rôle
  let menuItems = [];

  if (isMainAdmin){
    menuItems = adminMenuItems;
  } else if(isSubAdmin){
    menuItems = subAdminMenuItems;
  } else {
    menuItems = supAdminMenuItems;
  }
  

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar - Responsive */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed lg:relative w-64 bg-gradient-to-b from-gray-600 to-gray-600 text-white transition-all duration-300 flex flex-col h-full z-50 overflow-y-auto lg:translate-x-0 shadow-xl`}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center space-x-2">
            <img 
              src="/logo192.png" 
              alt="Stellone Market" 
              className="h-10 w-auto object-contain transition-transform duration-200 hover:scale-105"
            />
            <span className="text-xl font-bold tracking-tight">StelonneMarket</span>
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 hover:bg-gray-700 rounded-lg transition-all duration-200 hover:scale-105"
          >
            {sidebarOpen ? <FiX className="text-xl" /> : <FiMenu className="text-xl" />}
          </button>
        </div>

        {/* User Info */}
        <div className={`p-4 ${primaryLight} border-b border-gray-700`}>
          <div className="flex items-center space-x-3">
            <div className={`${primaryBg} w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl text-white shadow-md`}>
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-sm">Bienvenue,</p>
              <p className="text-sm opacity-90">{user?.name}</p>
              <p className={`text-xs ${secondaryColor} font-medium`}>
                {user?.role?.name}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setSidebarOpen(false);
              }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                window.location.pathname === item.path
                  ? `${primaryBg} text-white font-semibold shadow-md`
                  : 'hover:bg-gray-700 hover:shadow-sm'
              }`}
            >
              <item.icon className="text-xl flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-700 space-y-1">
          <button
            onClick={goToShop}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-gray-700 transition-all duration-200 hover:shadow-sm"
          >
            <FiShoppingBag className="text-xl flex-shrink-0" />
            <span className="text-sm">Voir la boutique</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-red-600 transition-all duration-200 hover:shadow-sm"
          >
            <FiLogOut className="text-xl flex-shrink-0" />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto flex flex-col">
        {/* Mobile Header avec Burger Menu */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between shadow-sm">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-200"
          >
            <FiMenu className="text-2xl" />
          </button>
          <div className="flex items-center space-x-2">
            <div className={`${primaryBg} p-2 rounded-xl shadow-md transition-transform duration-200 hover:scale-105`}>
              <FiShoppingBag className="text-white text-lg" />
            </div>
            <span className="font-bold text-gray-800 tracking-tight">StelonneMarket</span>
          </div>
          <div className="w-10"></div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          <div className={`rounded-2xl ${secondaryLight} shadow-sm p-4 md:p-6 mb-6`}>
            {isSupAdmin ? (
              <>
                <Routes>
                  <Route index element={<ProductManagement />} />
                </Routes>
              </>
            ) : (
              <>
                <Routes>
                  <Route index element={<DashboardHome />} />
                  <Route path="products" element={<ProductManagement />} />
                  <Route path="invoice/:orderId" element={<InvoicePage />} />
                  {isMainAdmin && (
                    <>
                      <Route path="suppliers" element={<SupplierManagement />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="statistics" element={<Statistics />} />
                    </>
                  )}
                  <Route path="orders" element={<OrderManagement />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Overlay - Ferme la sidebar au clic */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;